import { NexoyaTeamPlan, type NexoyaTeam } from '../types/types';

export const PLANS = {
  PREMIUM: 'premium',
  STARTER: 'starter',
} as const;

export type Plan = (typeof PLANS)[keyof typeof PLANS];

/**
 * The wire enum `TeamPlan` mapped to the internal slug.
 *
 * SLUGS, NOT THE WIRE VALUES — and that is a decision, not an oversight. Adopting
 * `'STARTER'` verbatim would delete this table, but the slug is also what
 * `setStarterPlanOverride` writes to `localStorage` and what `?preview_plan=` accepts
 * (`src/entitlements/starterPreviewOverride.ts`). Changing it would silently invalidate
 * every override already persisted in a support engineer's browser and every shared preview
 * URL — silently, because `resolveStarterPlanOverride` returns `null` for an
 * unrecognised value and `null` reads as "no override". One lookup table is the cheaper half
 * of that trade.
 * The slug did move to `'starter'`; `resolveStarterPlanOverride` still accepts legacy `'nexoya4agency'` on read.
 *
 * Keyed off the GENERATED enum, never literals: a backend rename then breaks this file at regen
 * time instead of failing open to PREMIUM for every Starter tenant. It has fired once for real:
 * `NEXOYA_4_AGENCY` became `STARTER`.
 *
 * A `Map`, not an object literal, for the same reason as `FEATURE_BY_TAB` in
 * `portfolioTabFeatures.ts`: `raw` is network data, and `literal[raw]` walks
 * `Object.prototype`, so `plan: "toString"` would resolve to the *function*
 * `Object.prototype.toString` — truthy, so the fail-open branch never fires and a function
 * escapes as a `Plan`. That exact bug already shipped once here via `?activeTab=`.
 */
const PLAN_FROM_WIRE: ReadonlyMap<string, Plan> = new Map<string, Plan>([
  [NexoyaTeamPlan.Premium, PLANS.PREMIUM],
  [NexoyaTeamPlan.Starter, PLANS.STARTER],
]);

/**
 * Translates one `Team.plan` value into a `Plan`.
 *
 * `raw` stays `string`, not `NexoyaTeamPlan`: a server that adds an enum member before this
 * frontend regenerates hands us a value TypeScript swears cannot exist.
 *
 * ⚠ DIVERGES FROM `parseRole` (`src/Auth/rbac/roles.ts`) ON PURPOSE: that returns
 * `Role | null`, this returns `Plan` and NEVER null. Fails OPEN — `null`, `undefined`, an
 * unrecognised member and a malformed value all resolve to PREMIUM. Returning null on an
 * unrecognised value is precisely the silent failure mode this boundary exists to prevent
 * (`parseRole` began returning null the day the backend and the FE disagreed on a spelling,
 * and nothing said so). Never lock a paying customer out of paid capability because of a
 * derivation failure; the API is the real enforcement.
 */
export function planFromWirePlan(raw: string | null | undefined): Plan {
  if (typeof raw !== 'string') return PLANS.PREMIUM;
  return PLAN_FROM_WIRE.get(raw) ?? PLANS.PREMIUM;
}

/**
 * `Partial`: a payload carries only what its document selected, whatever Apollo's type says.
 * `plan` is re-widened to `string` for the same reason `planFromWirePlan` takes one — the value
 * is network data, and the generated enum only describes what we last regenerated against.
 */
type TeamPlanPayload = (Omit<Partial<NexoyaTeam>, 'plan'> & { plan?: string | null }) | null | undefined;

/**
 * Resolves the plan for `teamId`, or `null` if it cannot be trusted yet.
 *
 * The `team_id` check is the team-switch guard. Apollo keeps the previous team's `data`
 * on screen while refetching, so without it a team switch would briefly render the old
 * team's tier — flashing Nexoya Starter upsell chrome at a PREMIUM customer, or unlocking
 * premium tabs for a Starter one. `null` maps to the `loading` entitlement state, which
 * renders neither. That is the ONLY reason this returns null: a team whose payload we do
 * trust always resolves to a plan, because `planFromWirePlan` fails open.
 *
 * This is also why `TEAM_QUERY` selects `team_id` alongside `plan` — drop it and this guard
 * compares `undefined` and returns null forever.
 */
export function planForTeam(teamId: number, team: TeamPlanPayload): Plan | null {
  if (!team || team.team_id !== teamId) return null;
  return planFromWirePlan(team.plan);
}
